import { Suspense, useEffect, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { HUDRoot } from './components/HUD/HUDRoot';
import { CinematicOverlay } from './components/Overlay/CinematicOverlay';
import { GlobeScene } from './scenes/GlobeScene';
import { useViewportStore } from './stores/useViewportStore';
import { useRealtimeStream } from './hooks/useRealtimeStream';
import { useIncidents } from './hooks/useIncidents';
import { useGPUTier } from './hooks/useGPUTier';

/**
 * Application shell.
 *
 * Layering (z-axis):
 *   0. <Canvas/>       — R3F scene graph (Globe)
 *   1. <CinematicOverlay/> — vignette + mission banner
 *   2. <HUDRoot/>      — interactive HUD (telemetry + command rail)
 *
 * Quality tier is detected once and pushed into the viewport store; scenes
 * read it to gate post-processing density. See docs/FRONTEND_PLAN.md.
 */
export function App(): JSX.Element {
  const gpuTier = useGPUTier();
  const setQuality = useViewportStore((s) => s.setQualityTier);

  useEffect(() => {
    setQuality(gpuTier === 'high' ? 'cinematic' : gpuTier === 'mid' ? 'balanced' : 'lite');
  }, [gpuTier, setQuality]);

  // Stand up the realtime uplink. The hook owns reconnect and store dispatch.
  useRealtimeStream();
  // Poll real incidents (IODA outages + provider status) into the globe.
  useIncidents();

  const dpr = useMemo<[number, number]>(
    () => (gpuTier === 'low' ? [1, 1.25] : gpuTier === 'mid' ? [1, 1.75] : [1, 2]),
    [gpuTier],
  );

  return (
    <div className="tiw-shell">
      <Canvas
        dpr={dpr}
        gl={{ antialias: gpuTier !== 'low', powerPreference: 'high-performance', alpha: false }}
        camera={{ position: [0, 0, 6], fov: 45, near: 0.1, far: 5000 }}
        flat
      >
        <color attach="background" args={[0x04060a]} />
        <Suspense fallback={null}>
          <GlobeScene />
        </Suspense>
      </Canvas>

      <CinematicOverlay />
      <HUDRoot />
    </div>
  );
}
